import { useState, useRef, type DragEvent, type ChangeEvent } from 'react'
import { useFileUpload, type UploadStatus } from '../hooks/useFileUpload'

interface FileUploadProps {
  sampleId: string
  onUploadComplete?: (fileId: string) => void
  acceptedExtensions?: string
  maxSizeMB?: number
}

const ALLOWED_EXTENSIONS = '.dna,.pdf,.png,.jpg,.jpeg,.txt,.csv,.xlsx,.docx'

export function FileUpload({
  sampleId,
  onUploadComplete,
  acceptedExtensions = ALLOWED_EXTENSIONS,
  maxSizeMB = 100,
}: FileUploadProps) {
  const { status, progress, error, uploadFile, reset } = useFileUpload()
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const maxSizeBytes = maxSizeMB * 1024 * 1024

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSizeBytes) {
      return `File size (${(file.size / 1024 / 1024).toFixed(1)} MB) exceeds maximum allowed (${maxSizeMB} MB)`
    }

    // Check file extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    const allowedExts = acceptedExtensions.split(',').map((e) => e.toLowerCase())
    if (!allowedExts.includes(ext)) {
      return `File type ${ext} not allowed. Accepted: ${acceptedExtensions}`
    }

    return null
  }

  const handleFileSelect = (file: File) => {
    const error = validateFile(file)
    if (error) {
      setValidationError(error)
      setSelectedFile(null)
    } else {
      setValidationError(null)
      setSelectedFile(file)
    }
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    const resultFileId = await uploadFile(selectedFile, sampleId)
    if (resultFileId) {
      onUploadComplete?.(resultFileId)
    }
  }

  const handleReset = () => {
    reset()
    setSelectedFile(null)
    setValidationError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const isUploading = status === 'requesting' || status === 'uploading' || status === 'confirming'
  const isComplete = status === 'success'
  const hasError = status === 'error' || validationError !== null

  return (
    <div style={styles.container}>
      {/* Drop zone */}
      <div
        style={{
          ...styles.dropZone,
          ...(isDragging ? styles.dropZoneActive : {}),
          ...(hasError ? styles.dropZoneError : {}),
          ...(isComplete ? styles.dropZoneSuccess : {}),
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedExtensions}
          onChange={handleInputChange}
          style={styles.hiddenInput}
          disabled={isUploading}
        />

        {isComplete ? (
          <div style={styles.successContent}>
            <span style={styles.successIcon}>OK</span>
            <span>File uploaded successfully</span>
            {selectedFile && <span style={styles.filename}>{selectedFile.name}</span>}
          </div>
        ) : isUploading ? (
          <div style={styles.uploadingContent}>
            <span>{getStatusText(status)}</span>
            <div style={styles.progressContainer}>
              <div style={{ ...styles.progressBar, width: `${progress}%` }} />
            </div>
            <span style={styles.progressText}>{progress}%</span>
          </div>
        ) : selectedFile ? (
          <div style={styles.selectedContent}>
            <span style={styles.filename}>{selectedFile.name}</span>
            <span style={styles.fileSize}>
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </span>
          </div>
        ) : (
          <div style={styles.emptyContent}>
            <span>Drag and drop a file here, or click to select</span>
            <span style={styles.hint}>
              Accepted: {acceptedExtensions} (max {maxSizeMB} MB)
            </span>
          </div>
        )}
      </div>

      {/* Error display */}
      {(error || validationError) && (
        <div style={styles.error}>{error || validationError}</div>
      )}

      {/* Action buttons */}
      <div style={styles.actions}>
        {!isComplete && selectedFile && !isUploading && (
          <button className="btn btn-primary" onClick={handleUpload}>
            Upload
          </button>
        )}
        {(selectedFile || isComplete || hasError) && !isUploading && (
          <button className="btn btn-secondary" onClick={handleReset}>
            {isComplete ? 'Upload Another' : 'Clear'}
          </button>
        )}
      </div>
    </div>
  )
}

function getStatusText(status: UploadStatus): string {
  switch (status) {
    case 'requesting':
      return 'Preparing upload...'
    case 'uploading':
      return 'Uploading to storage...'
    case 'confirming':
      return 'Confirming upload...'
    default:
      return 'Processing...'
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },
  dropZone: {
    border: '2px dashed var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--spacing-lg)',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'var(--transition-fast)',
    backgroundColor: 'var(--color-bg-secondary)',
  },
  dropZoneActive: {
    borderColor: 'var(--color-accent)',
    backgroundColor: 'var(--color-bg-tertiary)',
  },
  dropZoneError: {
    borderColor: 'var(--color-danger)',
  },
  dropZoneSuccess: {
    borderColor: 'var(--color-success)',
    cursor: 'default',
  },
  hiddenInput: {
    display: 'none',
  },
  emptyContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-sm)',
    color: 'var(--color-text-secondary)',
  },
  hint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
  selectedContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
  },
  filename: {
    fontFamily: 'var(--font-family-mono)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    wordBreak: 'break-all',
  },
  fileSize: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
  uploadingContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-sm)',
    alignItems: 'center',
  },
  progressContainer: {
    width: '100%',
    maxWidth: '300px',
    height: '8px',
    backgroundColor: 'var(--color-bg-tertiary)',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: 'var(--color-accent)',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
  successContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-sm)',
    alignItems: 'center',
    color: 'var(--color-success)',
  },
  successIcon: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'bold',
  },
  error: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'rgba(248, 81, 73, 0.1)',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-danger)',
    fontSize: 'var(--font-size-sm)',
  },
  actions: {
    display: 'flex',
    gap: 'var(--spacing-sm)',
    justifyContent: 'flex-end',
  },
}
